import { Component, inject, signal, TemplateRef, ViewChild } from '@angular/core'
import { PageHeaderComponent, PageHeaderMenuItem } from '../page-header/page-header.component'
import { PageContentComponent } from '../page-content/page-content.component'
import { ServicePointGroupService } from 'src/app/public/service-point-groups/service-point-group.service'
import { combineLatest, map, tap } from 'rxjs'
import { MatCard, MatCardActions, MatCardContent, MatCardHeader, MatCardTitle } from '@angular/material/card'
import { MatIcon } from '@angular/material/icon'
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu'
import { NgbModal } from '@ng-bootstrap/ng-bootstrap'
import { ServicePointGroupModal } from './service-point-group-modal.component'
import { ServicePointGroupFilterListComponent } from './service-point-group-filter-list.component'
import { TextInputComponent } from '../text-input/text-input.component'
import { ServicePointGroupUpsertRequest, ServicePointGroupWithRefs } from './service-point-group.types'
import { ToastService } from '../toasts/toasts.service'
import openModal from 'src/app/shared/services/openModal'
import { LabeledPropertyComponent } from '../labeled-property/labeled-property.component'
import { LabeledDisplayComponent } from '../labeled-display/labeled-display.component'
import { ConfirmationModalComponent } from '../modals/confirmation-modal/confirmation-modal.component'
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop'
import { FilterService } from 'src/app/shared/services/filter/filter.service'

@Component({
  selector: 'app-service-point-groups',
  imports: [
    PageHeaderComponent,
    PageContentComponent,
    MatCard,
    MatCardHeader,
    MatCardTitle,
    MatCardContent,
    MatCardActions,
    MatMenu,
    MatMenuItem,
    MatMenuTrigger,
    MatIcon,
    TextInputComponent,
    ServicePointGroupFilterListComponent,
    LabeledPropertyComponent,
    LabeledDisplayComponent,
  ],
  styles: `
    .card-grid {
        display: grid;
        grid-template-columns: 1fr;
        gap: 2rem;

        .card-content {
          height: 100%;
        }

        .card {
          border: 1px solid gainsboro;
        }

        *::ng-deep {
          .mat-mdc-card-header-text {
            width: 100%;
            border-bottom: 1px solid gainsboro;
            margin-bottom: 0.5rem;
          }
      }
    }

    .id-container {
        display: flex;
        flex-wrap: wrap;

        span {
            height: 1.5rem;
            line-height: 1rem;
            font-size: 0.9rem;
            margin: 0.25rem;
            padding: 0.25rem 0.5rem;
            border-radius: 0.75rem;
            background-color: gainsboro;
            font-style: italic;
        }
    }

    @media (min-width: 768px) {
        .search-container {
          max-width: 100%;
        }
        .card-grid {
            grid-template-columns:1fr;
        }
        .empty-message {
          margin-top: 4rem;
          font-size: 1.5rem;
          line-height: 2rem;
          color: green;
        }
    }

    @media (min-width: 1024px) {
      .search-container {
          max-width: 50%;
        }
        .card-grid {
            grid-template-columns:1fr 1fr;
        }
        .empty-message {
          margin-top: 4rem;
          font-size: 1.5rem;
          line-height: 2rem;
          color: green;
          grid-column: 1/3;
        }
    }

    @media (min-width: 1280px) {
        .search-container {
          max-width: 33%;
        }
        .card-grid {
            grid-template-columns: 1fr 1fr 1fr;
        }
        .empty-message {
          margin-top: 4rem;
          font-size: 1.5rem;
          line-height: 2rem;
          color: green;
          grid-column: 2;
        }
    }
  `,
  template: `
    <div app-page-header class="header" header="Service Point Groups" [menuItems]="pageHeaderMenuItems"></div>

    <app-page-content>
      <div
        app-text-input
        placeholder="Search"
        class="search-container"
        [value]="searchTerm()"
        (valueChange)="searchTerm.set($event)"
      ></div>
      <div class="card-grid">
        @if(!loading()) {
          @for(group of servicePointGroups(); track group.service_point_group_uid){
            <mat-card appearance="raised" class="card">
              <mat-card-header>
                <mat-card-title>
                  {{ group.service_point_group_name }}
                </mat-card-title>
              </mat-card-header>
              <mat-card-content class="card-content">
                <div class="id-container">
                  <app-service-point-group-filter-list
                    [groupId]="group.service_point_group_uid"
                  />
                </div>
              </mat-card-content>
              <mat-card-actions align="end">
                <mat-menu #menu="matMenu">
                  <button mat-menu-item (click)="openUpdateModal(group)">Edit</button>
                  <button mat-menu-item (click)="openDeleteModal(group)">Delete</button>
                </mat-menu>
                <button
                  class="action-btn icon borderless"
                  [matMenuTriggerFor]="menu"
                >
                  <mat-icon svgIcon="more_horizontal"></mat-icon>
                </button>
              </mat-card-actions>
            </mat-card>
          } @empty {
            <div class="empty-message">
              We couldn't find a service point group with a group name that starts with "{{searchTerm()}}"
            </div>
          }
        }
      </div>
    </app-page-content>

        <ng-template #deleteConfirmation let-ctx="ctx">
        Are you sure you want to delete this Service Point Group?
        @if(ctx) {
          <div app-labeled-property label="Group Name">
            <div app-labeled-display
              [value]="ctx.service_point_group_name">
            </div>
          </div>
          <app-service-point-group-filter-list [groupId]="ctx.service_point_group_uid" />
        }
    </ng-template>
  `,
})
export class ServicePointGroupsComponent {
  private readonly servicePointGroupsService = inject(ServicePointGroupService)
  private readonly modalService = inject(NgbModal)
  private readonly toastService = inject(ToastService)
  private readonly filterService = inject(FilterService)

  @ViewChild('deleteConfirmation')
  protected readonly deleteConfirmationTemplate: TemplateRef<{ ctx: ServicePointGroupWithRefs }>

  protected readonly loading = signal(true)
  protected readonly searchTerm= signal('')
  protected readonly servicePointGroups = signal<ServicePointGroupWithRefs[]>([])

  protected readonly pageHeaderMenuItems: readonly PageHeaderMenuItem[] = [
    { label: 'Create', onClick: () => this.openCreateModal() },
  ]

  constructor() {
    combineLatest([
      this.servicePointGroupsService.get(),
      toObservable(this.searchTerm).pipe(tap(() => this.loading.set(true))),
    ]).pipe(
      map(([groups, search]) => search 
        ? groups.filter(g => g.service_point_group_name.toLowerCase().startsWith(search.toLowerCase()))
        : groups,
      ),
      takeUntilDestroyed(),
    ).subscribe({
      next: (d) => {
        d.map(g => this.filterService.importServicePointGroup(g))
        this.servicePointGroups.set(d)
        this.loading.set(false)
      },
      error: (e) => {
        this.toastService.show({ type: 'error', message: e.detail ?? 'An unknown error occured' })
        this.loading.set(false)
      },
    })
  }
  
  openDeleteModal(data: ServicePointGroupWithRefs) {
    openModal(
      this.modalService,
      ConfirmationModalComponent,
      { centered: true },
    ).init({
      headerText: 'Confirm Deletion',
      confirmButtonText: 'Delete',
      bodyTemplate: this.deleteConfirmationTemplate,
      bodyContext: data,
      onCancel: (() => {
        this.modalService.dismissAll()
      }),
      onConfirm: () => this.delete(data.service_point_group_uid),
    })
  }

  delete(groupId: string) {
    this.servicePointGroupsService
      .delete(groupId)
      .subscribe({
        error: (e) => {
          this.toastService.show({ type: 'error', message: e.detail ?? 'An unknown error occured' })
        },
        complete: () => {
          this.toastService.show({ type: 'success', message: 'Deletion successful!' })
          this.modalService.dismissAll()
          this.servicePointGroupsService.refresh()
        },      
      })
  }

  openCreateModal() {
    openModal(this.modalService, ServicePointGroupModal, {
      centered: true,
      windowClass: 'confirmation-modal',
      backdrop: 'static',
    }).init({
      data: {},
      onSave: ({ request }) => {
        this.create(request)
      },
      onCancel: () => this.modalService.dismissAll(),
    })
  }

  private create(req: ServicePointGroupUpsertRequest) {
    this.servicePointGroupsService
      .create(req)
      .subscribe({
        error: (e) => {
          this.toastService.show({ type: 'error', message: e.detail ?? 'An unknown error occured' })
        },
        complete: () => {
          this.toastService.show({ type: 'success', message: 'Creation successful!' })
          this.modalService.dismissAll()
          this.servicePointGroupsService.refresh()
        },      
      })
  }

  openUpdateModal(data: ServicePointGroupWithRefs) {
    openModal(this.modalService, ServicePointGroupModal, {
      centered: true,
      windowClass: 'confirmation-modal',
      backdrop: 'static',
    }).init({
      data,
      onSave: ({ id, request }) => {
        this.update(id!, request)
      },
      onCancel: () => this.modalService.dismissAll(),
    })
  }

  private update(servicePointGroupUid: string, req: ServicePointGroupUpsertRequest) {
    this.servicePointGroupsService
      .update(servicePointGroupUid, req)
      .subscribe({
        error: (e) => {
          this.toastService.show({ type: 'error', message: e.detail ?? 'An unknown error occured' })
        },
        complete: () => {
          this.toastService.show({ type: 'success', message: 'Update successful!' })
          this.modalService.dismissAll()
          this.servicePointGroupsService.refresh()
        },      
      })
  }
}
